import { NgClass, NgStyle } from '@angular/common'
import { HttpClient } from '@angular/common/http'
import type { OnDestroy, OnInit } from '@angular/core'
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DOCUMENT, inject } from '@angular/core'
import { RouterLink } from '@angular/router'
import { Store } from '@ngrx/store'
import type { Observable } from 'rxjs'
import { combineLatest, Subscription } from 'rxjs'

import type { Account } from '@account/models'
import * as fromAccount from '@account/ngrx'
import { SiteNavContainerComponent } from '@app/components/member/site-nav-container/site-nav-container.component'
import { passAsync } from '@app/core/pass-async'
import { BadgesService } from '@app/modules/pwa/services/badges.service'
import { ModalLimitedComponent } from '@app/overlays/modal/limited/limited.component'
import { ModalService } from '@app/overlays/modal/modal.service'
import { ModalWhyVerifyComponent } from '@app/overlays/modal/why-verify/why-verify.component'
import { BreakpointService, BreakpointType } from '@app/services/breakpoint.service'
import { MemberService } from '@app/services/member.service'
import { RenderService } from '@app/services/render.service'
import { SiteStore } from '@app/store/site.store'
import * as fromUnreadInteractions from '@store/unread-interactions'

import { AssetUrlPipe } from '../../../modules/assets/pipes/asset-url.pipe'
import { CreditsNavigationDirective } from '../../../modules/navigation/directives/credits-navigation.directive'
import { ButtonComponent } from '../../../modules/ui/components/button/button.component'
import { IconComponent } from '../../../modules/ui/components/icon/icon.component'
import type * as fromRoot from '../../../store/ngrx/root'

@Component({
  selector: 'sb-member-profile-nav',
  templateUrl: './profile-nav.component.html',
  styleUrls: ['./profile-nav.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, NgStyle, IconComponent, CreditsNavigationDirective, NgClass, ButtonComponent, AssetUrlPipe],
})
export class MemberProfileNavComponent implements OnInit, OnDestroy {
  readonly memberService = inject(MemberService)
  private readonly modalService = inject(ModalService)
  private readonly changeDetectorRef = inject(ChangeDetectorRef)
  readonly siteNavContainerComponent = inject(SiteNavContainerComponent)
  private readonly httpClient = inject(HttpClient)
  private readonly badgesService = inject(BadgesService)
  private document = inject<Document>(DOCUMENT)
  readonly siteStore = inject(SiteStore)

  private readonly subscription = new Subscription()
  private readonly cdObservables: Observable<any>[] = []
  private readonly passAsync = passAsync(this.cdObservables, this.subscription)

  profilePhotoStyle: any
  account!: Account
  messagesDot!: boolean
  matchesDot!: boolean
  accessDot!: boolean
  balance!: number | null
  isMobile!: boolean
  busy = false

  constructor() {
    const memberService = this.memberService
    const renderService = inject(RenderService)
    const store = inject<Store<fromRoot.State>>(Store)
    const breakpointService = inject(BreakpointService)

    this.changeDetectorRef.detach()

    this.passAsync(memberService.account$, account => {
      this.account = account
      this.profilePhotoStyle = renderService.backgroundStyle(account.profile_photo_url!)
    })

    this.passAsync(store.select(fromUnreadInteractions.Selectors.hasAnyMessages), val => (this.messagesDot = val))
    this.passAsync(store.select(fromUnreadInteractions.Selectors.hasAnyMatches), val => (this.matchesDot = val))
    this.passAsync(store.select(fromUnreadInteractions.Selectors.hasAnyAccess), val => (this.accessDot = val))
    this.passAsync(store.select(fromAccount.Selectors.creditBalance), val => (this.balance = val))
    this.passAsync(breakpointService.below(BreakpointType.TABLET), val => (this.isMobile = val))
  }

  ngOnInit(): void {
    this.subscription.add(combineLatest(this.cdObservables).subscribe(() => this.changeDetectorRef.detectChanges()))
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe()
  }

  logout(event: MouseEvent): void {
    event.preventDefault()
    if (!this.busy) {
      this.busy = true
      this.changeDetectorRef.detectChanges()
      void this.badgesService.cleanBadges()
      window.location.href = `/logout`
    }
  }

  openLimited(): void {
    const { instance } = this.modalService.create(ModalLimitedComponent)
    instance.onConfirm(() => this.siteNavContainerComponent.close())
  }

  openWhyVerify(): void {
    const { instance } = this.modalService.create(ModalWhyVerifyComponent)
    instance.onConfirm(() => this.siteNavContainerComponent.close())
  }
}
